import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  ArrowLeft, Settings, BarChart3, Save, ExternalLink, CheckCircle, 
  AlertCircle, Monitor, Shield, Loader2, Users, UserCog, AlertTriangle,
  Wrench, RefreshCw, Bot, Eye, EyeOff, Bell, Mail, Calendar, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SiteSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSession, setSavingSession] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [savingHoneypot, setSavingHoneypot] = useState(false);
  const [savingRecaptcha, setSavingRecaptcha] = useState(false);
  const [savingContentProtection, setSavingContentProtection] = useState(false);
  const [contentProtectionEnabled, setContentProtectionEnabled] = useState(true);
  // iter 111: watermark
  const [savingWatermark, setSavingWatermark] = useState(false);
  const [watermark, setWatermark] = useState({
    watermark_enabled: true,
    watermark_show_indos: true,
    watermark_show_email: false,
    watermark_show_mobile: false,
    watermark_opacity: 0.08,
    watermark_density: 18,
  });
  
  // GA Settings
  const [settings, setSettings] = useState({
    ga_measurement_id: '',
    updated_at: null,
    updated_by: null
  });
  const [originalSettings, setOriginalSettings] = useState({});
  
  // Session Settings (separate for users and admins)
  const [sessionSettings, setSessionSettings] = useState({
    // User settings
    user_max_sessions: 1,
    user_limit_behavior: 'block',
    user_limit_message: '',
    // Admin settings
    admin_max_sessions: 3,
    admin_limit_behavior: 'auto_logout',
    // Meta
    updated_at: null,
    updated_by: null
  });
  const [originalSessionSettings, setOriginalSessionSettings] = useState({});

  // Maintenance Mode Settings
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceEndTime, setMaintenanceEndTime] = useState('');
  // Announcement fields (opt-in transactional notifications — no user opt-out)
  const [scheduledStartAt, setScheduledStartAt] = useState('');
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [notificationState, setNotificationState] = useState({});
  const [showWereBackDialog, setShowWereBackDialog] = useState(false);
  const [pendingMaintenanceSave, setPendingMaintenanceSave] = useState(false);
  const [originalMaintenance, setOriginalMaintenance] = useState({});

  // Honeypot Anti-Spam Settings
  const [honeypotSettings, setHoneypotSettings] = useState({
    honeypot_signup: true,
    honeypot_login: false,
    honeypot_forgot_password: true
  });
  const [originalHoneypotSettings, setOriginalHoneypotSettings] = useState({});

  // reCAPTCHA Settings
  const [recaptchaSettings, setRecaptchaSettings] = useState({
    recaptcha_enabled: false,
    recaptcha_site_key: '',
    recaptcha_secret_key: '',
    recaptcha_signup: true,
    recaptcha_login: false,
    recaptcha_forgot_password: true
  });
  const [originalRecaptchaSettings, setOriginalRecaptchaSettings] = useState({});

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch all settings in parallel
      const [gaResponse, sessionResponse, maintenanceResponse, honeypotResponse, recaptchaResponse, contentProtectionResponse, watermarkResponse] = await Promise.all([
        axios.get(`${API}/site-settings`),
        axios.get(`${API}/site-settings/session`),
        axios.get(`${API}/site-settings/maintenance`),
        axios.get(`${API}/site-settings/honeypot`),
        axios.get(`${API}/site-settings/recaptcha`),
        axios.get(`${API}/site-settings/content-protection`),
        axios.get(`${API}/site-settings/watermark`)
      ]);
      
      // GA Settings
      setSettings({
        ga_measurement_id: gaResponse.data.ga_measurement_id || '',
        updated_at: gaResponse.data.updated_at,
        updated_by: gaResponse.data.updated_by
      });
      setOriginalSettings({
        ga_measurement_id: gaResponse.data.ga_measurement_id || ''
      });

      // Content protection (iter 110)
      setContentProtectionEnabled(
        !!contentProtectionResponse.data.content_protection_enabled
      );

      // Watermark (iter 111)
      setWatermark({
        watermark_enabled: !!watermarkResponse.data.watermark_enabled,
        watermark_show_indos: !!watermarkResponse.data.watermark_show_indos,
        watermark_show_email: !!watermarkResponse.data.watermark_show_email,
        watermark_show_mobile: !!watermarkResponse.data.watermark_show_mobile,
        watermark_opacity: typeof watermarkResponse.data.watermark_opacity === 'number'
          ? watermarkResponse.data.watermark_opacity
          : 0.08,
        watermark_density: typeof watermarkResponse.data.watermark_density === 'number'
          ? watermarkResponse.data.watermark_density
          : 18,
      });
      
      // Session Settings
      const sessData = sessionResponse.data;
      setSessionSettings({
        user_max_sessions: sessData.user_max_sessions ?? 1,
        user_limit_behavior: sessData.user_limit_behavior || 'block',
        user_limit_message: sessData.user_limit_message || '',
        admin_max_sessions: sessData.admin_max_sessions ?? 3,
        admin_limit_behavior: sessData.admin_limit_behavior || 'auto_logout',
        updated_at: sessData.updated_at,
        updated_by: sessData.updated_by
      });
      setOriginalSessionSettings({
        user_max_sessions: sessData.user_max_sessions ?? 1,
        user_limit_behavior: sessData.user_limit_behavior || 'block',
        user_limit_message: sessData.user_limit_message || '',
        admin_max_sessions: sessData.admin_max_sessions ?? 3,
        admin_limit_behavior: sessData.admin_limit_behavior || 'auto_logout'
      });

      // Maintenance Settings
      const maintData = maintenanceResponse.data;
      setMaintenanceMode(maintData.maintenance_mode || false);
      setMaintenanceMessage(maintData.maintenance_message || '');
      setMaintenanceEndTime(maintData.maintenance_end_time || '');
      setScheduledStartAt(maintData.scheduled_start_at || '');
      setNotifyUsers(maintData.notify_users || false);
      setAnnouncementTitle(maintData.announcement_title || '');
      setAnnouncementMessage(maintData.announcement_message || '');
      setNotificationState(maintData.notification_state || {});
      setOriginalMaintenance({
        maintenance_mode: maintData.maintenance_mode || false,
        maintenance_message: maintData.maintenance_message || '',
        maintenance_end_time: maintData.maintenance_end_time || '',
        scheduled_start_at: maintData.scheduled_start_at || '',
        notify_users: maintData.notify_users || false,
        announcement_title: maintData.announcement_title || '',
        announcement_message: maintData.announcement_message || ''
      });

      // Honeypot Settings
      const honeypotData = honeypotResponse.data;
      setHoneypotSettings({
        honeypot_signup: honeypotData.honeypot_signup ?? true,
        honeypot_login: honeypotData.honeypot_login ?? false,
        honeypot_forgot_password: honeypotData.honeypot_forgot_password ?? true
      });
      setOriginalHoneypotSettings({
        honeypot_signup: honeypotData.honeypot_signup ?? true,
        honeypot_login: honeypotData.honeypot_login ?? false,
        honeypot_forgot_password: honeypotData.honeypot_forgot_password ?? true
      });

      // reCAPTCHA Settings
      const recaptchaData = recaptchaResponse.data;
      setRecaptchaSettings({
        recaptcha_enabled: recaptchaData.recaptcha_enabled ?? false,
        recaptcha_site_key: recaptchaData.recaptcha_site_key || '',
        recaptcha_secret_key: recaptchaData.recaptcha_secret_key || '',
        recaptcha_signup: recaptchaData.recaptcha_signup ?? true,
        recaptcha_login: recaptchaData.recaptcha_login ?? false,
        recaptcha_forgot_password: recaptchaData.recaptcha_forgot_password ?? true
      });
      setOriginalRecaptchaSettings({
        recaptcha_enabled: recaptchaData.recaptcha_enabled ?? false,
        recaptcha_site_key: recaptchaData.recaptcha_site_key || '',
        recaptcha_secret_key: recaptchaData.recaptcha_secret_key || '',
        recaptcha_signup: recaptchaData.recaptcha_signup ?? true,
        recaptcha_login: recaptchaData.recaptcha_login ?? false,
        recaptcha_forgot_password: recaptchaData.recaptcha_forgot_password ?? true
      });
      
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load site settings');
    } finally {
      setLoading(false);
    }
  };


  // iter 110: content-protection toggle. Optimistic update — flip UI
  // immediately, rollback on error. Save-button-less UX matches the
  // other Switch controls on this page (honeypot, recaptcha rows).
  const handleToggleContentProtection = async (nextEnabled) => {
    const previous = contentProtectionEnabled;
    setContentProtectionEnabled(nextEnabled);
    try {
      setSavingContentProtection(true);
      await axios.put(`${API}/site-settings/content-protection`, {
        content_protection_enabled: nextEnabled,
      });
      toast.success(
        nextEnabled ? 'Content protection enabled' : 'Content protection disabled'
      );
    } catch (err) {
      setContentProtectionEnabled(previous);
      toast.error(err.response?.data?.detail || 'Failed to update content protection');
    } finally {
      setSavingContentProtection(false);
    }
  };

  // iter 111: watermark settings. Optimistic PATCH — the payload is a
  // partial (only the field(s) that changed) to keep opacity-slider
  // scrubbing snappy without saving the whole config on every drag tick.
  const patchWatermark = async (patch) => {
    const previous = watermark;
    const next = { ...watermark, ...patch };
    setWatermark(next);
    try {
      setSavingWatermark(true);
      await axios.put(`${API}/site-settings/watermark`, patch);
    } catch (err) {
      setWatermark(previous);
      toast.error(err.response?.data?.detail || 'Failed to update watermark');
    } finally {
      setSavingWatermark(false);
    }
  };


  const handleSaveGA = async () => {
    try {
      setSaving(true);
      
      await axios.put(`${API}/site-settings`, {
        ga_measurement_id: settings.ga_measurement_id.trim() || null
      });
      
      toast.success('Analytics settings saved successfully');
      setOriginalSettings({
        ga_measurement_id: settings.ga_measurement_id.trim()
      });
      
      // Reload page to apply GA changes
      if (settings.ga_measurement_id !== originalSettings.ga_measurement_id) {
        toast.info('Reloading to apply analytics changes...');
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      const message = error.response?.data?.detail || 'Failed to save settings';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSession = async () => {
    try {
      setSavingSession(true);
      
      await axios.put(`${API}/site-settings/session`, {
        user_max_sessions: sessionSettings.user_max_sessions,
        user_limit_behavior: sessionSettings.user_limit_behavior,
        user_limit_message: sessionSettings.user_limit_message.trim() || null,
        admin_max_sessions: sessionSettings.admin_max_sessions,
        admin_limit_behavior: sessionSettings.admin_limit_behavior
      });
      
      toast.success('Session settings saved successfully');
      setOriginalSessionSettings({
        user_max_sessions: sessionSettings.user_max_sessions,
        user_limit_behavior: sessionSettings.user_limit_behavior,
        user_limit_message: sessionSettings.user_limit_message.trim(),
        admin_max_sessions: sessionSettings.admin_max_sessions,
        admin_limit_behavior: sessionSettings.admin_limit_behavior
      });
      
    } catch (error) {
      console.error('Failed to save session settings:', error);
      const message = error.response?.data?.detail || 'Failed to save session settings';
      toast.error(message);
    } finally {
      setSavingSession(false);
    }
  };

  // Actual PUT — split out so both the "just save" path and the
  // "we're back?" confirmation path can call it with the same payload
  // and one place to maintain the state-sync post-save.
  const persistMaintenance = async (notifyOnEnd) => {
    try {
      setSavingMaintenance(true);
      const res = await axios.put(`${API}/site-settings/maintenance`, {
        maintenance_mode: maintenanceMode,
        maintenance_message: maintenanceMessage.trim() || null,
        maintenance_end_time: maintenanceEndTime || null,
        scheduled_start_at: scheduledStartAt || null,
        notify_users: !!notifyUsers,
        announcement_title: announcementTitle.trim() || null,
        announcement_message: announcementMessage.trim() || null,
        notify_on_end: !!notifyOnEnd
      });

      if (notifyOnEnd && res.data?.ended_notification) {
        const s = res.data.ended_notification;
        toast.success(`"We're back" sent — email: ${s.sent_email}/${s.users}, in-app: ${s.sent_in_app}/${s.users}`);
      } else {
        toast.success(maintenanceMode ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
      }

      // Reset the local idempotency-state view — the backend cleared it if
      // the schedule changed, so pull the fresh state next time.
      setNotificationState(res.data?.maintenance_notification_state || {});
      setOriginalMaintenance({
        maintenance_mode: maintenanceMode,
        maintenance_message: maintenanceMessage.trim(),
        maintenance_end_time: maintenanceEndTime,
        scheduled_start_at: scheduledStartAt,
        notify_users: !!notifyUsers,
        announcement_title: announcementTitle.trim(),
        announcement_message: announcementMessage.trim()
      });
    } catch (error) {
      console.error('Failed to save maintenance settings:', error);
      const message = error.response?.data?.detail || 'Failed to save maintenance settings';
      toast.error(message);
    } finally {
      setSavingMaintenance(false);
      setShowWereBackDialog(false);
      setPendingMaintenanceSave(false);
    }
  };

  const handleSaveMaintenance = async () => {
    // If admin is toggling maintenance OFF (was on → now off), pause and
    // ask whether to fire the "we're back" transactional email + in-app.
    const wasOn = originalMaintenance.maintenance_mode;
    if (wasOn && !maintenanceMode) {
      setPendingMaintenanceSave(true);
      setShowWereBackDialog(true);
      return;
    }
    await persistMaintenance(false);
  };

  const handleSaveHoneypot = async () => {
    try {
      setSavingHoneypot(true);
      
      await axios.put(`${API}/site-settings/honeypot`, honeypotSettings);
      
      toast.success('Honeypot settings saved successfully');
      setOriginalHoneypotSettings({...honeypotSettings});
      
    } catch (error) {
      console.error('Failed to save honeypot settings:', error);
      const message = error.response?.data?.detail || 'Failed to save honeypot settings';
      toast.error(message);
    } finally {
      setSavingHoneypot(false);
    }
  };

  const handleSaveRecaptcha = async () => {
    try {
      setSavingRecaptcha(true);
      
      await axios.put(`${API}/site-settings/recaptcha`, recaptchaSettings);
      
      toast.success('reCAPTCHA settings saved successfully');
      setOriginalRecaptchaSettings({...recaptchaSettings});
      
    } catch (error) {
      console.error('Failed to save reCAPTCHA settings:', error);
      const message = error.response?.data?.detail || 'Failed to save reCAPTCHA settings';
      toast.error(message);
    } finally {
      setSavingRecaptcha(false);
    }
  };

  const hasGAChanges = settings.ga_measurement_id !== originalSettings.ga_measurement_id;
  const isValidGAId = !settings.ga_measurement_id || settings.ga_measurement_id.startsWith('G-');
  
  const hasSessionChanges = 
    sessionSettings.user_max_sessions !== originalSessionSettings.user_max_sessions ||
    sessionSettings.user_limit_behavior !== originalSessionSettings.user_limit_behavior ||
    sessionSettings.user_limit_message !== originalSessionSettings.user_limit_message ||
    sessionSettings.admin_max_sessions !== originalSessionSettings.admin_max_sessions ||
    sessionSettings.admin_limit_behavior !== originalSessionSettings.admin_limit_behavior;

  const hasMaintenanceChanges = 
    maintenanceMode !== originalMaintenance.maintenance_mode ||
    maintenanceMessage !== originalMaintenance.maintenance_message ||
    maintenanceEndTime !== originalMaintenance.maintenance_end_time ||
    scheduledStartAt !== originalMaintenance.scheduled_start_at ||
    notifyUsers !== originalMaintenance.notify_users ||
    announcementTitle !== originalMaintenance.announcement_title ||
    announcementMessage !== originalMaintenance.announcement_message;

  const hasHoneypotChanges = 
    honeypotSettings.honeypot_signup !== originalHoneypotSettings.honeypot_signup ||
    honeypotSettings.honeypot_login !== originalHoneypotSettings.honeypot_login ||
    honeypotSettings.honeypot_forgot_password !== originalHoneypotSettings.honeypot_forgot_password;

  const hasRecaptchaChanges = 
    recaptchaSettings.recaptcha_enabled !== originalRecaptchaSettings.recaptcha_enabled ||
    recaptchaSettings.recaptcha_site_key !== originalRecaptchaSettings.recaptcha_site_key ||
    recaptchaSettings.recaptcha_secret_key !== originalRecaptchaSettings.recaptcha_secret_key ||
    recaptchaSettings.recaptcha_signup !== originalRecaptchaSettings.recaptcha_signup ||
    recaptchaSettings.recaptcha_login !== originalRecaptchaSettings.recaptcha_login ||
    recaptchaSettings.recaptcha_forgot_password !== originalRecaptchaSettings.recaptcha_forgot_password;

  const formatSessionText = (maxSessions, behavior) => {
    if (maxSessions === 0) {
      return 'can login on unlimited devices simultaneously.';
    } else if (maxSessions === 1) {
      return `can only be logged in on 1 device at a time. ${behavior === 'block' ? 'New logins will be blocked.' : 'Oldest session will be logged out.'}`;
    } else {
      return `can be logged in on up to ${maxSessions} devices simultaneously. ${behavior === 'block' ? 'Additional logins will be blocked.' : 'Oldest session will be logged out when limit is exceeded.'}`;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/tools')}
            className="shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-[#6366F1]" />
              Site Settings
            </h1>
            <p className="text-gray-600">Configure global site settings</p>
          </div>
        </div>

        {/* Content Protection (iter 110) — disables copy/select/right-click
            on 5 user-facing surfaces (oral QB, written QB, notes,
            practice sessions, class archive). Admins always exempt. */}
        <Card className="mb-6" data-testid="content-protection-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Copy className="w-6 h-6 text-slate-700" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl">Content Protection</CardTitle>
                <CardDescription>
                  Discourage casual copy-paste of proprietary content on user pages
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-gray-50">
              <div className="min-w-0">
                <div className="font-medium text-gray-900">
                  Disable copy on user pages
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  When enabled, users cannot select text, right-click, or
                  press Ctrl+C on: <span className="font-medium">Oral
                  Question Bank</span>, <span className="font-medium">Written
                  Question Bank</span>, <span className="font-medium">Notes</span>,
                  {' '}<span className="font-medium">Practice Sessions</span>, and
                  {' '}<span className="font-medium">Class Archive</span>.
                  Admins are always exempt.
                </p>
              </div>
              <Switch
                checked={contentProtectionEnabled}
                disabled={savingContentProtection}
                onCheckedChange={handleToggleContentProtection}
                data-testid="content-protection-switch"
              />
            </div>
            <div className="p-3 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900 flex gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Friction, not prevention.</span>{' '}
                This blocks casual Ctrl+C but cannot stop screenshots, OCR,
                browser dev-tools, or a determined user retyping the content.
                Treat it as a signal that content is proprietary, not as DRM.
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Watermark (iter 111) — diagonal-tiled overlay with viewer
            identity to leak into screenshots. Independent from copy
            block: admin can turn either on/off. */}
        <Card className="mb-6" data-testid="watermark-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <EyeOff className="w-6 h-6 text-indigo-700" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl">Watermark</CardTitle>
                <CardDescription>
                  Overlay viewer identity across protected pages so screenshots leak the leaker
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-gray-50">
              <div className="min-w-0">
                <div className="font-medium text-gray-900">Enable watermark</div>
                <p className="text-sm text-gray-600 mt-1">
                  Same 5 surfaces as content protection. Guests see{' '}
                  <code className="text-xs bg-white px-1 rounded border">HelmTorq + IP</code>.
                  Logged-in users see the fields you enable below. Admins are always exempt.
                </p>
              </div>
              <Switch
                checked={watermark.watermark_enabled}
                disabled={savingWatermark}
                onCheckedChange={(v) => patchWatermark({ watermark_enabled: v })}
                data-testid="watermark-enabled-switch"
              />
            </div>

            {watermark.watermark_enabled && (
              <>
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="text-sm font-medium text-gray-900">
                    Show for logged-in users
                  </div>
                  <p className="text-xs text-gray-600 -mt-2">
                    The more you show, the more personal info leaks in a screenshot — a stronger deterrent to sharing.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={watermark.watermark_show_indos}
                        onCheckedChange={(v) => patchWatermark({ watermark_show_indos: !!v })}
                        data-testid="watermark-show-indos"
                      />
                      <span>INDoS number</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={watermark.watermark_show_email}
                        onCheckedChange={(v) => patchWatermark({ watermark_show_email: !!v })}
                        data-testid="watermark-show-email"
                      />
                      <span>Email</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={watermark.watermark_show_mobile}
                        onCheckedChange={(v) => patchWatermark({ watermark_show_mobile: !!v })}
                        data-testid="watermark-show-mobile"
                      />
                      <span>Mobile number</span>
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900">Opacity</div>
                    <div className="text-sm tabular-nums text-gray-700">
                      {Math.round(watermark.watermark_opacity * 100)}%
                    </div>
                  </div>
                  <Slider
                    value={[watermark.watermark_opacity]}
                    min={0.02}
                    max={0.25}
                    step={0.01}
                    onValueCommit={(v) => patchWatermark({ watermark_opacity: v[0] })}
                    onValueChange={(v) =>
                      setWatermark((w) => ({ ...w, watermark_opacity: v[0] }))
                    }
                    data-testid="watermark-opacity-slider"
                  />
                  <p className="text-xs text-gray-500">
                    Faint (2%) is barely visible during use but still readable in a screenshot.
                    Bold (25%) is always visible — good for extreme cases but distracting to read through.
                  </p>
                </div>

                {/* iter 111b: Density slider — controls how many tiles
                    are rendered. Snaps to layout stops so admins get
                    predictable grids regardless of slider position. */}
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900">Density</div>
                    <div className="text-sm tabular-nums text-gray-700">
                      {(() => {
                        const d = watermark.watermark_density;
                        if (d <= 8) return 'Very sparse';
                        if (d <= 14) return 'Sparse';
                        if (d <= 20) return 'Balanced';
                        if (d <= 26) return 'Standard';
                        if (d <= 38) return 'Dense';
                        return 'Very dense';
                      })()}
                      {' '}
                      <span className="text-gray-500">({watermark.watermark_density} tiles)</span>
                    </div>
                  </div>
                  <Slider
                    value={[watermark.watermark_density]}
                    min={6}
                    max={48}
                    step={6}
                    onValueCommit={(v) => patchWatermark({ watermark_density: v[0] })}
                    onValueChange={(v) =>
                      setWatermark((w) => ({ ...w, watermark_density: v[0] }))
                    }
                    data-testid="watermark-density-slider"
                  />
                  <p className="text-xs text-gray-500">
                    Fewer tiles are less cluttered but easier to crop out of a screenshot.
                    Balanced (18) is the recommended default when you enable all three identity fields.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Google Analytics Settings */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Google Analytics 4</CardTitle>
                <CardDescription>
                  Track website traffic and user behavior
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ga_id">Measurement ID</Label>
              <Input
                id="ga_id"
                placeholder="G-XXXXXXXXXX"
                value={settings.ga_measurement_id}
                onChange={(e) => setSettings(prev => ({ ...prev, ga_measurement_id: e.target.value }))}
                className={!isValidGAId ? 'border-red-500' : ''}
              />
              {!isValidGAId && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Measurement ID should start with &quot;G-&quot;
                </p>
              )}
              <p className="text-sm text-gray-500">
                Enter your GA4 Measurement ID to enable traffic analytics. Leave empty to disable.
              </p>
            </div>

            {/* Status Indicator */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
              {settings.ga_measurement_id ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-green-700">Analytics Enabled</span>
                  <Badge variant="outline" className="ml-2">{settings.ga_measurement_id}</Badge>
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Analytics Disabled</span>
                </>
              )}
            </div>

            {/* Help Section */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">How to get your Measurement ID:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">Google Analytics <ExternalLink className="w-3 h-3" /></a></li>
                <li>Create a new property or select existing one</li>
                <li>Go to Admin → Data Streams → Web</li>
                <li>Copy the Measurement ID (starts with G-)</li>
              </ol>
            </div>

            {/* Last Updated Info */}
            {settings.updated_at && (
              <p className="text-xs text-gray-400">
                Last updated: {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by}
              </p>
            )}
            
            {/* Save Button for GA */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setSettings({ ...settings, ga_measurement_id: originalSettings.ga_measurement_id })}
                disabled={!hasGAChanges || saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveGA}
                disabled={!hasGAChanges || saving || !isValidGAId}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Analytics
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Session Management Settings */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Monitor className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Session Management</CardTitle>
                  <CardDescription>
                    Control how many devices users and admins can be logged in on simultaneously
                  </CardDescription>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/tools/sessions')}
                className="shrink-0"
              >
                <Monitor className="w-4 h-4 mr-2" />
                View Active Sessions
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Regular Users Section */}
            <div className="p-4 border rounded-lg bg-gray-50/50">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Regular Users</h3>
                <Badge variant="outline" className="text-xs">Subscribers</Badge>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                {/* User Max Sessions */}
                <div className="space-y-2">
                  <Label htmlFor="user_max_sessions">Max Concurrent Sessions</Label>
                  <Select
                    value={String(sessionSettings.user_max_sessions)}
                    onValueChange={(value) => setSessionSettings(prev => ({ 
                      ...prev, 
                      user_max_sessions: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger id="user_max_sessions">
                      <SelectValue placeholder="Select limit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 device (strict)</SelectItem>
                      <SelectItem value="2">2 devices</SelectItem>
                      <SelectItem value="3">3 devices</SelectItem>
                      <SelectItem value="0">Unlimited</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* User Behavior */}
                <div className="space-y-2">
                  <Label>When limit exceeded</Label>
                  <RadioGroup
                    value={sessionSettings.user_limit_behavior}
                    onValueChange={(value) => setSessionSettings(prev => ({ 
                      ...prev, 
                      user_limit_behavior: value 
                    }))}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="block" id="user_block" />
                      <Label htmlFor="user_block" className="font-normal cursor-pointer">Block</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="auto_logout" id="user_auto" />
                      <Label htmlFor="user_auto" className="font-normal cursor-pointer">Auto-logout oldest</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* User Custom message (only shown for block behavior) */}
              {sessionSettings.user_limit_behavior === 'block' && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="user_message">Custom Error Message</Label>
                  <Textarea
                    id="user_message"
                    placeholder="You are already logged in on another device. Please logout there first."
                    value={sessionSettings.user_limit_message}
                    onChange={(e) => setSessionSettings(prev => ({ 
                      ...prev, 
                      user_limit_message: e.target.value 
                    }))}
                    rows={2}
                  />
                </div>
              )}
            </div>

            {/* Admins Section */}
            <div className="p-4 border rounded-lg bg-indigo-50/50 border-indigo-200">
              <div className="flex items-center gap-2 mb-4">
                <UserCog className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-gray-900">Admins & Sub-Admins</h3>
                <Badge variant="outline" className="text-xs bg-indigo-100 text-indigo-700 border-indigo-300">Staff</Badge>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Admin Max Sessions */}
                <div className="space-y-2">
                  <Label htmlFor="admin_max_sessions">Max Concurrent Sessions</Label>
                  <Select
                    value={String(sessionSettings.admin_max_sessions)}
                    onValueChange={(value) => setSessionSettings(prev => ({ 
                      ...prev, 
                      admin_max_sessions: parseInt(value) 
                    }))}
                  >
                    <SelectTrigger id="admin_max_sessions">
                      <SelectValue placeholder="Select limit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 device</SelectItem>
                      <SelectItem value="2">2 devices</SelectItem>
                      <SelectItem value="3">3 devices</SelectItem>
                      <SelectItem value="0">Unlimited</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Admin Behavior */}
                <div className="space-y-2">
                  <Label>When limit exceeded</Label>
                  <RadioGroup
                    value={sessionSettings.admin_limit_behavior}
                    onValueChange={(value) => setSessionSettings(prev => ({ 
                      ...prev, 
                      admin_limit_behavior: value 
                    }))}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="block" id="admin_block" />
                      <Label htmlFor="admin_block" className="font-normal cursor-pointer">Block</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="auto_logout" id="admin_auto" />
                      <Label htmlFor="admin_auto" className="font-normal cursor-pointer">Auto-logout oldest</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </div>

            {/* Current Configuration Summary */}
            <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="space-y-2">
                  <h4 className="font-medium text-purple-900">Current Configuration</h4>
                  <div className="text-sm text-purple-800 space-y-1">
                    <p>
                      <strong>Regular Users</strong> {formatSessionText(sessionSettings.user_max_sessions, sessionSettings.user_limit_behavior)}
                    </p>
                    <p>
                      <strong>Admins</strong> {formatSessionText(sessionSettings.admin_max_sessions, sessionSettings.admin_limit_behavior)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Updated Info */}
            {sessionSettings.updated_at && (
              <p className="text-xs text-gray-400">
                Last updated: {new Date(sessionSettings.updated_at).toLocaleString()} by {sessionSettings.updated_by}
              </p>
            )}

            {/* Save Button for Session */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setSessionSettings({ 
                  ...sessionSettings, 
                  ...originalSessionSettings
                })}
                disabled={!hasSessionChanges || savingSession}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSession}
                disabled={!hasSessionChanges || savingSession}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {savingSession ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Session Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Maintenance Mode Card */}
        <Card className="mb-6 border-2 border-amber-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${maintenanceMode ? 'bg-amber-500' : 'bg-amber-100'}`}>
                <Wrench className={`w-6 h-6 ${maintenanceMode ? 'text-white' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-xl">Maintenance Mode</CardTitle>
                  {maintenanceMode && (
                    <Badge className="bg-amber-500 text-white">ACTIVE</Badge>
                  )}
                </div>
                <CardDescription>
                  Block non-admin access during maintenance
                </CardDescription>
              </div>
              <Switch
                checked={maintenanceMode}
                onCheckedChange={setMaintenanceMode}
                className="data-[state=checked]:bg-amber-500"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {maintenanceMode && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Site is in maintenance mode!</p>
                    <p>All non-admin users are blocked from accessing the site. Currently logged-in users will be logged out.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="maintenance_message">Custom Maintenance Message</Label>
              <Textarea
                id="maintenance_message"
                placeholder="We're currently performing scheduled maintenance. Please check back later."
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maintenance_end_time">Estimated End Time (optional)</Label>
              <Input
                id="maintenance_end_time"
                type="datetime-local"
                value={maintenanceEndTime}
                onChange={(e) => setMaintenanceEndTime(e.target.value)}
                data-testid="maintenance-end-time-input"
              />
              <p className="text-xs text-gray-500">
                Displayed to users so they know when to check back
              </p>
            </div>

            {/* ---- Scheduled announcement (opt-in, transactional) ---- */}
            <div className="pt-4 mt-2 border-t border-amber-100">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-sky-100 rounded-lg mt-0.5">
                  <Bell className="w-5 h-5 text-sky-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">User Notification (optional)</h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Send a service email + in-app notification to every user. Transactional — users cannot opt out.
                  </p>
                </div>
                <Switch
                  checked={notifyUsers}
                  onCheckedChange={setNotifyUsers}
                  className="data-[state=checked]:bg-sky-600"
                  data-testid="notify-users-switch"
                />
              </div>

              {notifyUsers && (
                <div className="mt-4 space-y-4 pl-1">
                  <div className="space-y-2">
                    <Label htmlFor="scheduled_start_at" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      Scheduled Start (for reminder timing)
                    </Label>
                    <Input
                      id="scheduled_start_at"
                      type="datetime-local"
                      value={scheduledStartAt}
                      onChange={(e) => setScheduledStartAt(e.target.value)}
                      data-testid="scheduled-start-input"
                    />
                    <p className="text-xs text-gray-500">
                      Users get one heads-up 24 hours before this time, and a second &ldquo;starting now&rdquo; email at the exact time. If start time is within the next 24 hours, only the T-0 email fires. Leave blank to fire nothing (useful if you&apos;re announcing via a different channel).
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="announcement_title">Email Title</Label>
                    <Input
                      id="announcement_title"
                      placeholder="Scheduled Maintenance"
                      value={announcementTitle}
                      onChange={(e) => setAnnouncementTitle(e.target.value)}
                      data-testid="announcement-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="announcement_message">Email Message</Label>
                    <Textarea
                      id="announcement_message"
                      placeholder="What's happening, why, and any expected impact."
                      value={announcementMessage}
                      onChange={(e) => setAnnouncementMessage(e.target.value)}
                      rows={3}
                      data-testid="announcement-message-input"
                    />
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                      <Mail className="w-3 h-3" />
                      Uses the <code className="text-xs bg-gray-100 px-1 rounded">maintenance_scheduled</code> template — edit copy under Admin → Email Templates.
                    </p>
                  </div>

                  {/* Dispatch history — surfaces what's already fired so the
                      admin can see whether reschedule → re-fires or not. */}
                  {(notificationState?.sent_24h || notificationState?.sent_start || notificationState?.sent_end) && (
                    <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1">
                      <p className="font-medium text-gray-700 mb-1">Announcement history (current window):</p>
                      {notificationState.sent_24h && <p>• T-24h reminder sent at {new Date(notificationState.sent_24h).toLocaleString()}</p>}
                      {notificationState.sent_start && <p>• T-0 &ldquo;starting now&rdquo; sent at {new Date(notificationState.sent_start).toLocaleString()}</p>}
                      {notificationState.sent_end && <p>• &ldquo;We&apos;re back&rdquo; sent at {new Date(notificationState.sent_end).toLocaleString()}</p>}
                      <p className="text-gray-500 pt-1">Changing the scheduled start time clears this history and re-arms the reminders.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Save Button for Maintenance */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setMaintenanceMode(originalMaintenance.maintenance_mode);
                  setMaintenanceMessage(originalMaintenance.maintenance_message);
                  setMaintenanceEndTime(originalMaintenance.maintenance_end_time);
                  setScheduledStartAt(originalMaintenance.scheduled_start_at);
                  setNotifyUsers(originalMaintenance.notify_users);
                  setAnnouncementTitle(originalMaintenance.announcement_title);
                  setAnnouncementMessage(originalMaintenance.announcement_message);
                }}
                disabled={!hasMaintenanceChanges || savingMaintenance}
                data-testid="maintenance-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMaintenance}
                disabled={!hasMaintenanceChanges || savingMaintenance}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {savingMaintenance ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {maintenanceMode ? 'Enable Maintenance' : 'Save Settings'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* "We're back" confirmation — appears when admin toggles maintenance
            OFF and clicks Save. Answers must be explicit; there is no
            silent default because the choice fans out to every user's inbox. */}
        <AlertDialog open={showWereBackDialog} onOpenChange={(open) => {
          if (!open) { setShowWereBackDialog(false); setPendingMaintenanceSave(false); }
        }}>
          <AlertDialogContent data-testid="were-back-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Send a &ldquo;We&apos;re back&rdquo; notification?</AlertDialogTitle>
              <AlertDialogDescription>
                You&apos;re turning maintenance mode <strong>off</strong>. Would you like to send a
                service email + in-app notification to every user letting them know the site is back?
                <br/><br/>
                <span className="text-xs text-gray-500">
                  Uses the <code className="bg-gray-100 px-1 rounded">maintenance_ended</code> template.
                  This is transactional (no user opt-out) so it will reach every account with a valid email.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => persistMaintenance(false)}
                disabled={!pendingMaintenanceSave || savingMaintenance}
                data-testid="were-back-skip-btn"
              >
                No, just save
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => persistMaintenance(true)}
                disabled={!pendingMaintenanceSave || savingMaintenance}
                className="bg-sky-600 hover:bg-sky-700"
                data-testid="were-back-send-btn"
              >
                Yes, send notification
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Honeypot Anti-Spam Card */}
        <Card className="mb-6 border-2 border-green-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Shield className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Honeypot Anti-Spam Protection</CardTitle>
                <CardDescription>
                  Hidden fields to detect and block bot submissions
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Info Box */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-800">
              <p className="font-medium mb-1">How it works:</p>
              <p>A hidden field is added to forms. Bots fill it (humans do not see it), and submissions are rejected.</p>
              <p className="mt-2 text-xs text-green-600">Tip: Disable login honeypot when you need automated testing.</p>
            </div>

            {/* Toggle Options */}
            <div className="space-y-3">
              {/* Sign-up Form */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <UserCog className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Sign-up Form</span>
                    <p className="text-xs text-gray-500">Block bot registrations</p>
                  </div>
                </div>
                <Switch
                  checked={honeypotSettings.honeypot_signup}
                  onCheckedChange={(checked) => setHoneypotSettings(prev => ({...prev, honeypot_signup: checked}))}
                />
              </div>

              {/* Login Form */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Login Form</span>
                    <p className="text-xs text-gray-500">Optional - disable for automated testing</p>
                  </div>
                </div>
                <Switch
                  checked={honeypotSettings.honeypot_login}
                  onCheckedChange={(checked) => setHoneypotSettings(prev => ({...prev, honeypot_login: checked}))}
                />
              </div>

              {/* Forgot Password Form */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Forgot Password Form</span>
                    <p className="text-xs text-gray-500">Prevent spam reset requests</p>
                  </div>
                </div>
                <Switch
                  checked={honeypotSettings.honeypot_forgot_password}
                  onCheckedChange={(checked) => setHoneypotSettings(prev => ({...prev, honeypot_forgot_password: checked}))}
                />
              </div>
            </div>

            {/* Save Button for Honeypot */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setHoneypotSettings({...originalHoneypotSettings})}
                disabled={!hasHoneypotChanges || savingHoneypot}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveHoneypot}
                disabled={!hasHoneypotChanges || savingHoneypot}
                className="bg-green-600 hover:bg-green-700"
              >
                {savingHoneypot ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Honeypot Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* reCAPTCHA Card */}
        <Card className="mb-6 border-2 border-blue-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${recaptchaSettings.recaptcha_enabled ? 'bg-blue-500' : 'bg-blue-100'}`}>
                <Bot className={`w-6 h-6 ${recaptchaSettings.recaptcha_enabled ? 'text-white' : 'text-blue-600'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-xl">Google reCAPTCHA v2 Invisible</CardTitle>
                  {recaptchaSettings.recaptcha_enabled && (
                    <Badge className="bg-blue-500 text-white">ACTIVE</Badge>
                  )}
                </div>
                <CardDescription>
                  Advanced bot protection with Google reCAPTCHA
                </CardDescription>
              </div>
              <Switch
                checked={recaptchaSettings.recaptcha_enabled}
                onCheckedChange={(checked) => setRecaptchaSettings(prev => ({...prev, recaptcha_enabled: checked}))}
                className="data-[state=checked]:bg-blue-500"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Info Box */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
              <p className="font-medium mb-1">How it works:</p>
              <p>reCAPTCHA v2 Invisible runs in the background. Most users won&apos;t see anything - only suspicious activity triggers a challenge.</p>
              <p className="mt-2 text-xs text-blue-600">Tip: Works alongside Honeypot for double protection without extra friction.</p>
            </div>

            {/* API Keys Section */}
            <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-900">API Keys</h4>
              
              <div className="space-y-2">
                <Label htmlFor="recaptcha_site_key">Site Key</Label>
                <Input
                  id="recaptcha_site_key"
                  placeholder="Enter your reCAPTCHA Site Key"
                  value={recaptchaSettings.recaptcha_site_key}
                  onChange={(e) => setRecaptchaSettings(prev => ({...prev, recaptcha_site_key: e.target.value}))}
                />
                <p className="text-xs text-gray-500">Used in the frontend to render the reCAPTCHA widget</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recaptcha_secret_key">Secret Key</Label>
                <Input
                  id="recaptcha_secret_key"
                  type="password"
                  placeholder="Enter your reCAPTCHA Secret Key"
                  value={recaptchaSettings.recaptcha_secret_key}
                  onChange={(e) => setRecaptchaSettings(prev => ({...prev, recaptcha_secret_key: e.target.value}))}
                />
                <p className="text-xs text-gray-500">Used in the backend to verify reCAPTCHA responses (kept secret)</p>
              </div>

              {/* Help Section */}
              <div className="p-3 bg-white rounded-lg border text-sm">
                <h5 className="font-medium text-gray-900 mb-2">How to get your keys:</h5>
                <ol className="text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline inline-flex items-center gap-1">Google reCAPTCHA Admin <ExternalLink className="w-3 h-3" /></a></li>
                  <li>Create a new site with <strong>reCAPTCHA v2 &rarr; Invisible reCAPTCHA badge</strong></li>
                  <li>Add your domain(s) to the allowed list</li>
                  <li>Copy the Site Key and Secret Key</li>
                </ol>
              </div>
            </div>

            {/* Form Toggles */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Apply to Forms</h4>
              
              {/* Sign-up Form */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <UserCog className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Sign-up Form</span>
                    <p className="text-xs text-gray-500">Protect against bot registrations</p>
                  </div>
                </div>
                <Switch
                  checked={recaptchaSettings.recaptcha_signup}
                  onCheckedChange={(checked) => setRecaptchaSettings(prev => ({...prev, recaptcha_signup: checked}))}
                  disabled={!recaptchaSettings.recaptcha_enabled}
                />
              </div>

              {/* Login Form */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Login Form</span>
                    <p className="text-xs text-gray-500">Optional - may add friction to legitimate users</p>
                  </div>
                </div>
                <Switch
                  checked={recaptchaSettings.recaptcha_login}
                  onCheckedChange={(checked) => setRecaptchaSettings(prev => ({...prev, recaptcha_login: checked}))}
                  disabled={!recaptchaSettings.recaptcha_enabled}
                />
              </div>

              {/* Forgot Password Form */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Forgot Password Form</span>
                    <p className="text-xs text-gray-500">Prevent spam reset requests</p>
                  </div>
                </div>
                <Switch
                  checked={recaptchaSettings.recaptcha_forgot_password}
                  onCheckedChange={(checked) => setRecaptchaSettings(prev => ({...prev, recaptcha_forgot_password: checked}))}
                  disabled={!recaptchaSettings.recaptcha_enabled}
                />
              </div>
            </div>

            {/* Validation Warning */}
            {recaptchaSettings.recaptcha_enabled && (!recaptchaSettings.recaptcha_site_key || !recaptchaSettings.recaptcha_secret_key) && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Keys required</p>
                    <p>Both Site Key and Secret Key are required to enable reCAPTCHA protection.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button for reCAPTCHA */}
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                variant="outline"
                onClick={() => setRecaptchaSettings({...originalRecaptchaSettings})}
                disabled={!hasRecaptchaChanges || savingRecaptcha}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveRecaptcha}
                disabled={!hasRecaptchaChanges || savingRecaptcha}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingRecaptcha ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save reCAPTCHA Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SiteSettingsPage;
